import React, { useEffect, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Chip } from "primereact/chip";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import OrderService from "./service/OrderService"; // ← adjust path if different
import "./Configure.css"; // optional custom tweaks

/**
 * Generic util – mutates (add/remove) a nested array safely.
 */
const updateNestedArray = (obj, pathArr, fn) => {
  if (pathArr.length === 0) return;
  const lastKey = pathArr.pop();
  const parent = pathArr.reduce((acc, key) => acc[key], obj);
  parent[lastKey] = fn(parent[lastKey]);
};

const ArrayEditor = ({ path, items, onAdd, onRemove }) => {
  const [input, setInput] = useState("");

  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <div className="cfg-item-list">
        {items.map((it, idx) => (
          <Chip
            key={idx}
            label={it}
            removable
            onRemove={() => onRemove(path, idx)}
          />
        ))}
      </div>
      <div className="p-inputgroup" style={{ marginTop: ".5rem" }}>
        <InputText
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Add new…"
        />
        <Button
          icon="pi pi-plus"
          onClick={() => {
            onAdd(path, input.trim());
            setInput("");
          }}
          disabled={!input.trim()}
        />
      </div>
    </div>
  );
};

const ObjectArrayEditor = ({ path, items, onAdd, onRemove }) => {
  const [form, setForm] = useState({ size: "", price: "", types: "" });

  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <div style={{ display: "grid", gap: "0.5rem" }}>
        {items.map((obj, idx) => (
          <div
            key={idx}
            className="object-array-row"
          >
            <span style={{ flex: 1 }}><strong>Size:</strong> {obj.size}</span>
            <span style={{ flex: 1 }}><strong>Price:</strong> {obj.price}</span>
            <span style={{ flex: 2 }}>
              <strong>Types:</strong>{" "}
              {Array.isArray(obj.types) ? obj.types.join(", ") : obj.types}
            </span>
            <Button
              icon="pi pi-trash"
              className="p-button-sm p-button-danger"
              onClick={() => onRemove(path, idx)}
            />
          </div>
        ))}
      </div>

      <div
        className="p-inputgroup"
        style={{
          marginTop: "1rem",
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        <InputText
          placeholder="Size"
          value={form.size}
          onChange={(e) => setForm({ ...form, size: e.target.value })}
        />
        <InputText
          placeholder="Price"
          type="number"
          value={form.price}
          onChange={(e) => setForm({ ...form, price: e.target.value })}
        />
        <InputText
          placeholder="Types (comma-separated)"
          value={form.types}
          onChange={(e) => setForm({ ...form, types: e.target.value })}
        />
        <Button
          icon="pi pi-plus"
          disabled={!form.size}
          onClick={() => {
            onAdd(path, {
              size: form.size,
              price: parseFloat(form.price),
              types: form.types.split(",").map((t) => t.trim()),
            });
            setForm({ size: "", price: "", types: "" });
          }}
        />
      </div>
    </div>
  );
};



const Configure = () => {
  const [loading, setLoading] = useState(true);
  const [productProps, setProductProps] = useState(null); // full doc
  const [editBuffer, setEditBuffer] = useState({});
  const toast = React.useRef(null);

  /** ───────────  INITIAL FETCH  ─────────── */
  useEffect(() => {
    (async () => {
      try {
        const res = await OrderService.getProductProperties();
        const doc = res[0]; // only one document in collection
        setProductProps(doc);
        setEditBuffer(JSON.parse(JSON.stringify(doc))); // deep-clone for edits
      } catch (err) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Cannot load product properties",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /** ───────────  ADD ITEM  ─────────── */
  const addItem = (pathStr, value) => {
    if (!value) return;
    setEditBuffer((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      updateNestedArray(clone, pathStr.split("."), (arr) => [...arr, value]);
      return clone;
    });
  };

  /** ───────────  REMOVE ITEM  ─────────── */
  const removeItem = (pathStr, index) => {
    setEditBuffer((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      updateNestedArray(clone, pathStr.split("."), (arr) =>
        arr.filter((_, i) => i !== index)
      );
      return clone;
    });
  };

  /** ───────────  SAVE TO DB  ─────────── */
 const handleSave = async () => {
  const cleaned = JSON.parse(JSON.stringify(editBuffer), (key, value) => {
    if (key === "_id") return undefined;
    return value;
  });

  try {
    await OrderService.updateProductProperties(productProps._id, cleaned);
    setProductProps(cleaned);
    toast.current.show({
      severity: "success",
      summary: "Saved",
      detail: "Product settings updated",
    });
  } catch (err) {
    toast.current.show({
      severity: "error",
      summary: "Error",
      detail: "Save failed",
    });
  }
};



  /** ───────────  UI  ─────────── */
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "4rem" }}>
        <ProgressSpinner />
      </div>
    );
  }
  if (!productProps) return null;

  return (
    <div className="configure-container" style={{ padding: "1rem", height: "calc(100vh - 4rem)", overflowY: "auto" }}>
      <Toast ref={toast} />
      <h2>Configure Bakery Defaults</h2>
      <Accordion multiple activeIndex={[0, 1, 2, 3]}>
        {Object.entries(editBuffer)
          .filter(([k]) => k !== "_id")
          .map(([category, sub]) => (
            <AccordionTab
              key={category}
              header={category.charAt(0).toUpperCase() + category.slice(1)}
            >
              <div className="cfg-category-grid">
              {Object.entries(sub).map(([subKey, value]) => {
                // 1) pure primitives?  safe to chip-edit
                if (Array.isArray(value) && value.every(item => typeof item !== 'object')) {
                  return (
                    <div key={subKey}>
                      <h4>{subKey}</h4>
                      <ArrayEditor
                        path={`${category}.${subKey}`}
                        items={value}
                        onAdd={addItem}
                        onRemove={removeItem}
                      />
                    </div>
                  );
                }

                // 2) array of objects?  just list the .size fields
                if (Array.isArray(value) && value.every(item => typeof item === "object" && !Array.isArray(item))) {
                return (
                  <div key={subKey}>
                    <h4>{subKey}</h4>
                    <ObjectArrayEditor
                      path={`${category}.${subKey}`}
                      items={value}
                      onAdd={addItem}
                      onRemove={removeItem}
                    />
                  </div>
                );
              }


                // 3) anything else: skip
                return null;
              })}
            </div>
            </AccordionTab>
          ))}
      </Accordion>
      <div style={{ marginTop: "1rem", textAlign: "right" }}>
        <Button label="Save" icon="pi pi-save" onClick={handleSave} />
      </div>
    </div>
  );
};

export default Configure;
